import { get, find } from 'lodash';
import { STANDARD_GRADE_LEVEL } from 'utils/config/reference';

export const getStateNameFromCode = (code) => {
  if (!code) return '';
  const codeUpper = String(code).toUpperCase();
  const stateConfig = STANDARD_GRADE_LEVEL.find(s => String(s.code || '').toUpperCase() === codeUpper);
  if (stateConfig) {
    return  stateConfig.state;
  }
  if (codeUpper === 'ACTFL') {
    return 'Common Core';
  }
  return '';
};
export const getStateNameDisplayForCode = (code) => {
  if (!code) return '';
  const codeUpper = String(code).toUpperCase();
  const stateConfig = STANDARD_GRADE_LEVEL.find(s => String(s.code || '').toUpperCase() === codeUpper || String(s.state || '').toUpperCase() === codeUpper);
  if (stateConfig) {
    return  stateConfig.Description || stateConfig.state;
  }
  if (codeUpper === 'ACTFL') {
    return 'Common Core';
  }
  return '';
};

export const getStateCodeFromName = (stateName) => {
  if (!stateName) return '';
  const stateNameUpper = String(stateName).toUpperCase();
  const normalizedName = stateNameUpper === 'COMMON CORE' || stateNameUpper === 'COMMONCORE' ? 'Common Core' : stateNameUpper;
  const stateConfig = STANDARD_GRADE_LEVEL.find(s => String(s.state || '').toUpperCase() === normalizedName);
  return stateConfig ? stateConfig.code : '';
};

export const isIllinoisOrCommonCoreState = (state) => {
  if (!state) return false;
  const normalizedStateName = normalizeStateForComparison(state);
  return normalizedStateName === 'ILLINOIS' || normalizedStateName === 'COMMON CORE';
};

export const normalizeStateForComparison = (state) => {
  if (!state) return '';
  const stateUpper = String(state).toUpperCase();
  const stateName = getStateNameFromCode(stateUpper);
  return String(stateName || stateUpper).toUpperCase();
};

export const statesMatch = (state1, state2) => {
  if (!state1 || !state2) return false;
  const normalized1 = normalizeStateForComparison(state1);
  const normalized2 = normalizeStateForComparison(state2);
  
  if (normalized1 === normalized2) return true;
  
  const isCommonCore1 = normalized1 === 'COMMON CORE';
  const isCommonCore2 = normalized2 === 'COMMON CORE';
  
  return isCommonCore1 && isCommonCore2;
};

/**
 * Normalizes grade level strings to a standard format for comparison
 * Examples:
 * - "11th", "11", "11th grade" -> "11"
 * - "11-12", "11/12", "11th/12", "11/12th", "11th-12th" -> "11-12"
 * - "9-10", "9/10", "9th/10", "9/10th" -> "9-10"
 * - "9th", "9" -> "9" (but will be grouped with "9-10" if it exists)
 * - "Pre-K", "Pre-K" -> "Pre-K"
 * - "K", "K" -> "K"
 */
export const normalizeGradeLevel = (gradeLevel) => {
  if (gradeLevel == null || gradeLevel === '') return gradeLevel;
  const gradeStr = typeof gradeLevel === 'string' ? gradeLevel : String(gradeLevel);

  // Handle Pre-K and K separately
  if (gradeStr.toLowerCase() === 'pre-k' || gradeStr.toLowerCase() === 'prek') {
    return 'Pre-K';
  }
  if (gradeStr.toLowerCase() === 'k' || gradeStr.toLowerCase() === 'kindergarten') {
    return 'K';
  }
  
  // Remove common suffixes and words
  let normalized = gradeStr
    .toLowerCase()
    .replace(/\s*(th|st|nd|rd|grade)\s*/gi, '')
    .trim();
  
  // Normalize separators: /, -, to, through -> -
  normalized = normalized.replace(/[\s\/]+to[\s\/]+/gi, '-');
  normalized = normalized.replace(/[\s\/]+through[\s\/]+/gi, '-');
  normalized = normalized.replace(/[\s\/]+/g, '-');
  
  // Split by dash to handle ranges
  const parts = normalized.split('-').map(p => p.trim()).filter(p => p);
  
  if (parts.length === 1) {
    // Single grade: just return the number
    return parts[0];
  } else if (parts.length === 2) {
    // Range: return as "X-Y"
    return `${parts[0]}-${parts[1]}`;
  }
  
  // If more than 2 parts, return original normalized
  return normalized;
};

/**
 * Gets the grouping key for a grade level - single grades are grouped with their range if it exists
 * Examples:
 * - "9" and "9-10" -> both return "9-10" (if range exists)
 * - "11" and "11-12" -> both return "11-12" (if range exists)
 * - "9" alone -> returns "9"
 */
export const getGradeGroupingKey = (gradeLevel, allGrades = []) => {
  const normalized = normalizeGradeLevel(gradeLevel);
  
  // If it's already a range, return it
  if (normalized.includes('-')) {
    return normalized;
  }
  
  // If it's a single grade, check if there's a range that starts with this grade
  const matchingRange = allGrades.find(g => {
    const normalizedG = normalizeGradeLevel(g);
    if (normalizedG.includes('-')) {
      const [start] = normalizedG.split('-');
      return start === normalized;
    }
    return false;
  });
  
  // If we found a matching range, use that as the grouping key
  if (matchingRange) {
    return normalizeGradeLevel(matchingRange);
  }
  
  // Otherwise, return the normalized single grade
  return normalized;
};

/**
 * Compares two grade levels, treating variations as equal
 */
export const compareGradeLevels = (grade1, grade2) => {
  if (!grade1 || !grade2) return false;
  return normalizeGradeLevel(grade1) === normalizeGradeLevel(grade2);
};

/**
 * Compares grade levels for Illinois and Common Core states
 * Treats 9, 10, 9-10, 9/10, 9th/10, 9/10th, 9th, 10th as equivalent
 * Treats 11, 12, 11-12, 11/12, 11th/12, 11/12th, 11th, 12th as equivalent
 * For other states, uses standard compareGradeLevels
 */
export const compareGradesForIllinoisCommonCore = (grade1, grade2, state) => {
  if (!grade1 || !grade2) return false;
  
  if (!isIllinoisOrCommonCoreState(state)) {
    return compareGradeLevels(grade1, grade2);
  }
  
  const normalized1 = normalizeGradeLevel(grade1);
  const normalized2 = normalizeGradeLevel(grade2);
  
  if (normalized1 === normalized2) return true;
  
  const getGradeNumbers = (grade) => {
    const normalized = normalizeGradeLevel(grade);
    if (normalized.includes('-')) {
      const parts = normalized.split('-').map(p => parseInt(p.trim())).filter(p => !isNaN(p));
      return parts.length === 2 ? parts : [];
    }
    const num = parseInt(normalized);
    return isNaN(num) ? [] : [num];
  };
  
  const grades1 = getGradeNumbers(grade1);
  const grades2 = getGradeNumbers(grade2);
  
  if (grades1.length === 0 || grades2.length === 0) {
    return compareGradeLevels(grade1, grade2);
  }
  
  if (grades1.length === 1 && grades2.length === 1) {
    return grades1[0] === grades2[0];
  }
  
  if (grades1.length === 2 && grades2.length === 1) {
    return grades2[0] >= grades1[0] && grades2[0] <= grades1[1];
  }
  
  if (grades1.length === 1 && grades2.length === 2) {
    return grades1[0] >= grades2[0] && grades1[0] <= grades2[1];
  }
  
  if (grades1.length === 2 && grades2.length === 2) {
    return (grades1[0] <= grades2[1] && grades1[1] >= grades2[0]);
  }
  
  return false;
};

export const getStandardDescription = (standard, enabledStateCodes = []) => {
  if (!standard) return null;
  
  const description = get(standard, 'description');
  
  // Always return the description since each standard has its own state
  // The enabledStateCodes filter is handled at the listing level, not at the description level
  return description || null;
};

export const getEnabledStateNames = (organization) => {
  if (!organization) return [];
  
  const standardState = get(organization, 'standard_state', []);
  
  if (!Array.isArray(standardState)) return [];
  
  return standardState
    .filter(state => state.enabled === true)
    .map(state => {
      const code = state.code;
      return getStateNameFromCode(code) || code;
    })
    .filter(Boolean);
};

export const getStandardAbbr = (standard, enabledStateCodes = []) => {
  if (!standard) return null;
  
  const standardState = get(standard, 'state');
  const abbr = get(standard, 'abbr');
  
  if (enabledStateCodes && enabledStateCodes.length > 0) {
    const standardStateName = normalizeStateForComparison(standardState);
    const enabledStateNames = enabledStateCodes.map(code => normalizeStateForComparison(code)).filter(Boolean);
    
    const isEnabled = enabledStateNames.some(enabledName => statesMatch(standardState, enabledName));
    if (!isEnabled) {
      return null;
    }
  }
  
  return abbr;
};

export const formatStandardDisplay = (standardAbbr, standards = [], enabledStateCodes = []) => {
  if (!standardAbbr) return null;
  
  if (typeof standardAbbr === 'string' && standardAbbr.includes(',')) {
    const standardAbbrs = standardAbbr.split(',').map(s => s.trim()).filter(s => s);
    const formattedAbbrs = standardAbbrs.map(abbrItem => {
      const standardObj = find(standards, ["abbr", abbrItem]);
      return getStandardAbbr(standardObj, enabledStateCodes) || abbrItem;
    }).filter(Boolean);
    
    return formattedAbbrs.length > 0 ? formattedAbbrs.join(', ') : standardAbbr;
  } else {
    const standardObj = find(standards, ["abbr", typeof standardAbbr === 'string' ? standardAbbr.trim() : standardAbbr]);
    return getStandardAbbr(standardObj, enabledStateCodes) || standardAbbr;
  }
};

export const formatStateStandardDisplay = (stateStandard, standards = [], enabledStateCodes = []) => {
  if (!stateStandard || !Array.isArray(stateStandard) || stateStandard.length === 0) {
    return null;
  }
  
  const filteredStateStandard = filterStateStandardByEnabledStates(stateStandard, enabledStateCodes);
  
  if (filteredStateStandard.length === 0) {
    return null;
  }
  
  const formattedParts = [];
  
  filteredStateStandard.forEach((stateStd) => {
    if (!stateStd || !Array.isArray(stateStd.standard) || stateStd.standard.length === 0) {
      return;
    }
    
    const standardAbbrs = stateStd.standard.map(abbr => {
      const standardObj = find(standards, ["abbr", abbr]);
      return getStandardAbbr(standardObj, enabledStateCodes) || abbr;
    }).filter(Boolean);
    
    if (standardAbbrs.length > 0) {
      formattedParts.push(standardAbbrs.join(', '));
    }
  });
  
  return formattedParts.length > 0 ? formattedParts.join(' | ') : null;
};

export const getAllStandardAbbrsFromStateStandard = (stateStandard, enabledStateCodes = []) => {
  if (!stateStandard || !Array.isArray(stateStandard) || stateStandard.length === 0) {
    return [];
  }
  
  const filteredStateStandard = filterStateStandardByEnabledStates(stateStandard, enabledStateCodes);
  
  const allAbbrs = [];
  filteredStateStandard.forEach((stateStd) => {
    if (stateStd && Array.isArray(stateStd.standard)) {
      allAbbrs.push(...stateStd.standard);
    }
  });
  
  return [...new Set(allAbbrs)];
};

export const getStateStandardGroupedByState = (stateStandard) => {
  if (!stateStandard || !Array.isArray(stateStandard) || stateStandard.length === 0) {
    return {};
  }
  
  const grouped = {};
  stateStandard.forEach((stateStd) => {
    if (!stateStd || !stateStd.standard_name) return;
    
    const stateName = normalizeStateForComparison(stateStd.standard_name);
    if (!grouped[stateName]) {
      grouped[stateName] = [];
    }
    
    grouped[stateName].push({
      grade: stateStd.grade,
      standards: Array.isArray(stateStd.standard) ? stateStd.standard : []
    });
  });
  
  return grouped;
};

export const formatStateNameForDisplay = (stateCode) => {
  if (!stateCode) return '';
  
  const stateName = getStateNameFromCode(stateCode);
  return stateName || stateCode;
};

export const filterStateStandardByEnabledStates = (stateStandard, enabledStateCodes = []) => {
  if (!stateStandard || !Array.isArray(stateStandard) || stateStandard.length === 0) {
    return [];
  }
  
  const enabledStateNames = enabledStateCodes.map(code => normalizeStateForComparison(code)).filter(Boolean);
  const commonCoreStateName = normalizeStateForComparison('COMMONCORE');
  
  return stateStandard.filter((stateStd) => {
    if (!stateStd || !stateStd.standard_name) return false;
    
    const stateStdName = normalizeStateForComparison(stateStd.standard_name);
    
    if (enabledStateNames.length === 0) {
      return false;
    }
    
    return enabledStateNames.some(enabledName => {
      const enabledNameNormalized = normalizeStateForComparison(enabledName);
      return stateStdName === enabledNameNormalized || statesMatch(stateStd.standard_name, enabledName);
    });
  });
};

export const formatGradeLevelForDisplay = (grade, stateCode) => {
  if (!grade) return grade;
  
  if (!isIllinoisOrCommonCoreState(stateCode)) {
    return grade;
  }
  
  const gradeStr = String(grade).trim();
  
  const normalizedGrade = gradeStr.replace(/\s*(th|st|nd|rd|grade)\s*/gi, '').trim();
  const gradeNumbers = normalizedGrade.split(/[,\-\/]/).map(g => parseInt(g.trim())).filter(g => !isNaN(g));
  
  if (gradeNumbers.length === 1) {
    const singleGrade = gradeNumbers[0];
    if (singleGrade === 9 || singleGrade === 10) {
      return '9-10';
    }
    if (singleGrade === 11 || singleGrade === 12) {
      return '11-12';
    }
    return grade;
  }
  
  if (gradeNumbers.length === 2) {
    const grade1 = gradeNumbers[0];
    const grade2 = gradeNumbers[1];
    
    if ((grade1 === 9 && grade2 === 10) || (grade1 === 10 && grade2 === 9)) {
      return '9-10';
    }
    if ((grade1 === 11 && grade2 === 12) || (grade1 === 12 && grade2 === 11)) {
      return '11-12';
    }
    return `${Math.min(grade1, grade2)}-${Math.max(grade1, grade2)}`;
  }
  
  return grade;
};